// Captures a single frame from a self-hosted <video> file and returns it as
// a JPEG data URL, for use as a grid thumbnail when a post has no featured
// image set. Runs entirely client-side (no ffmpeg / server processing).
//
// Because this decodes actual video data, results are cached in
// localStorage per video URL so repeat visits don't re-generate the same
// frame. Cache entries are capped in size and count; failures are silent
// (caller just falls back to the plain dark placeholder).

const CACHE_PREFIX = 'vg_thumb_v1:'
const MAX_CACHE_ENTRIES = 40
const THUMB_MAX_WIDTH = 640
const SEEK_SECONDS = 1
const LOAD_TIMEOUT_MS = 8000

function cacheKey(url: string): string {
	return CACHE_PREFIX + url
}

function readCache(url: string): string | null {
	try {
		return window.localStorage.getItem(cacheKey(url))
	} catch {
		return null
	}
}

function writeCache(url: string, dataUrl: string): void {
	try {
		// Keep the cache from growing without bound: if we're at the cap,
		// drop the oldest-looking entry (simple FIFO via key iteration).
		const keys: string[] = []
		for (let i = 0; i < window.localStorage.length; i++) {
			const k = window.localStorage.key(i)
			if (k?.startsWith(CACHE_PREFIX)) keys.push(k)
		}
		if (keys.length >= MAX_CACHE_ENTRIES) {
			window.localStorage.removeItem(keys[0])
		}
		window.localStorage.setItem(cacheKey(url), dataUrl)
	} catch {
		// Quota exceeded or storage disabled — just skip caching.
	}
}

export async function captureVideoFrame(videoUrl: string): Promise<string | null> {
	const cached = readCache(videoUrl)
	if (cached) return cached

	return new Promise<string | null>(resolve => {
		const video = document.createElement('video')
		video.crossOrigin = 'anonymous'
		video.preload = 'auto'
		video.muted = true
		video.playsInline = true
		video.src = videoUrl

		let settled = false
		const finish = (result: string | null) => {
			if (settled) return
			settled = true
			clearTimeout(timer)
			video.removeAttribute('src')
			video.load()
			resolve(result)
		}

		const timer = setTimeout(() => finish(null), LOAD_TIMEOUT_MS)

		video.addEventListener('error', () => finish(null))

		video.addEventListener('loadedmetadata', () => {
			const duration = isFinite(video.duration) ? video.duration : 0
			const seekTo = duration > 0 ? Math.min(SEEK_SECONDS, duration * 0.5) : 0
			try {
				video.currentTime = seekTo
			} catch {
				finish(null)
			}
		})

		video.addEventListener('seeked', () => {
			try {
				const scale = video.videoWidth > THUMB_MAX_WIDTH ? THUMB_MAX_WIDTH / video.videoWidth : 1
				const canvas = document.createElement('canvas')
				canvas.width = Math.round(video.videoWidth * scale) || THUMB_MAX_WIDTH
				canvas.height = Math.round(video.videoHeight * scale) || Math.round(THUMB_MAX_WIDTH * 0.5625)

				const ctx = canvas.getContext('2d')
				if (!ctx) {
					finish(null)
					return
				}

				ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
				const dataUrl = canvas.toDataURL('image/jpeg', 0.75)
				writeCache(videoUrl, dataUrl)
				finish(dataUrl)
			} catch {
				// Most likely a tainted canvas (cross-origin video without
				// proper CORS headers) — fall back silently.
				finish(null)
			}
		})
	})
}
